import { useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'

import { useToken } from '../auth/hooks'
import { BASE_URL } from '../constants'

import useRecurrantFetch from '../hooks/useRecurrantFetch'

import { PREFERENCES } from '../user/constants'
import { useUserPreference } from '../user/hooks'
import { useIsSelected } from '../tabs/hooks'

import { useMap } from '../map/hooks'
import { useTab } from '../tabs/hooks'

import {
  fetchSuccess,
  fetchStart,
  fetchFail,
  fetchIncidentsFail,
  fetchIncidentsStart,
  fetchIncidentsSuccess,
  showIncident,
  hideIncident,
  selectEvents,
  nextEvent,
  prevEvent,
  clearSelection,
  openEditor,
  setAddress,
  setDescription,
  setIncidentId,
  setPosition,
  setSubject,
  setError,
  addEvent,
  editEvent,
} from './actions'

import {
  getEventById,
  getEvents,
  getVisibleIncidents,
  getIncidentIds,
  getIncidents,
  getIncidentById,
  getSigEventIds,
  getSigEventsIsLoading,
  getCurrentSelectedSigEvent,
  getVisibleIncidentIds,
  getSelectedEventIds,
  getVisibleEvents,
  getCurrentSelectedIndex,
  getSelectedCount,
  getEditor,
} from './selectors'

import { useEsri } from '../map/hooks'
import { LOCATOR_URL } from '../map/constants'

const esriReqs = ['esri/tasks/Locator', 'esri/geometry/SpatialReference']

export const useSigEventsFetch = () => {
  const dispatch = useDispatch()

  return useRecurrantFetch(`${BASE_URL}api/sigevents`, {
    onStart: () => dispatch(fetchStart()),
    onSuccess: (events) => dispatch(fetchSuccess(events)),
    onError: (error) => dispatch(fetchFail(error)),
  })
}

export const useSigEventsIncidentsFetch = () => {
  const dispatch = useDispatch()

  return useRecurrantFetch(`${BASE_URL}api/sigevents/incidents/active`, {
    onStart: () => dispatch(fetchIncidentsStart()),
    onSuccess: (incidents) => dispatch(fetchIncidentsSuccess(incidents)),
    onError: (error) => dispatch(fetchIncidentsFail(error)),
  })
}

export const useSigEvent = (eventId) => {
  const dispatch = useDispatch()
  const event = useSelector(getEventById)(eventId)

  const select = useCallback(() => dispatch(selectEvents([eventId])), [
    dispatch,
    eventId,
  ])

  return [event, { select }]
}

export const useCreateSigEvent = () => {
  const dispatch = useDispatch()
  const token = useToken()
  const event = useSelector(getEditor)
  const [{ view }] = useMap()
  const [ready, { Locator, SpatialReference }] = useEsri(esriReqs)
  const [_, goToList] = useTab('incidentList')

  const locator = useMemo(() => {
    if (ready) {
      return new Locator({
        url: LOCATOR_URL,
        outSpatialReference: new SpatialReference({ wkid: 4326 }),
      })
    } else return null
  }, [ready, Locator, SpatialReference])

  const actions = useMemo(
    () => ({
      setAddress: (address) => dispatch(setAddress(address)),
      setDescription: (description) => dispatch(setDescription(description)),
      setIncidentId: (incidentId) => dispatch(setIncidentId(incidentId)),
      setSubject: (subject) => dispatch(setSubject(subject)),
      setPosition: (longitude, latitude, address) =>
        dispatch(setPosition(longitude, latitude, address)),
      create: async () => {
        if (!event.longitude || !event.latitude) {
          dispatch(
            setError(
              'Please click the map or search an address to set a location.'
            )
          )
        } else if (event.incidentId == -1) {
          dispatch(setError('Please select an Incident'))
        } else if (!event.subject) {
          dispatch(setError('You must give the event a subject'))
        } else {
          const response = await fetch(`${BASE_URL}api/sigevents`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ ...event, error: undefined }),
          })
          if (response.ok) {
            const sigEvent = await response.json()
            dispatch(addEvent(sigEvent))
            goToList()
          } else {
            dispatch(setError(response.statusText))
          }
        }
      },
      searchAddress: async () => {
        if (ready && locator) {
          const locations = await locator.addressToLocations({
            address: {
              SingleLine: event && event.address,
            },
          })
          if (locations.length) {
            const addr = locations[0].address
            const location = locations[0].location
            dispatch(setPosition(location.longitude, location.latitude, addr))
          } else {
            dispatch(setError('Address not found.'))
          }
        }
      },
      zoomTo: () => {
        if (view && event.latitude && event.longitude) {
          const zoom = view.zoom > 15 ? view.zoom : 15
          view.goTo({
            center: [event.longitude, event.latitude],
            zoom,
          })
        }
      },
    }),
    [dispatch, token, event, ready, locator, view, goToList]
  )

  return [event, actions]
}

export const useEditSigEvent = () => {
  const dispatch = useDispatch()
  const token = useToken()
  const event = useSelector(getEditor)
  const [{ view }] = useMap()
  const [ready, { Locator, SpatialReference }] = useEsri(esriReqs)
  const [_, goToList] = useTab('incidentList')

  const locator = useMemo(() => {
    if (ready) {
      return new Locator({
        url: LOCATOR_URL,
        outSpatialReference: new SpatialReference({ wkid: 4326 }),
      })
    } else return null
  }, [ready, Locator, SpatialReference])

  const actions = useMemo(
    () => ({
      setAddress: (address) => dispatch(setAddress(address)),
      setDescription: (description) => dispatch(setDescription(description)),
      setSubject: (subject) => dispatch(setSubject(subject)),
      setPosition: (longitude, latitude, address) =>
        dispatch(setPosition(longitude, latitude, address)),
      edit: async () => {
        if (!event.longitude || !event.latitude) {
          dispatch(
            setError(
              'Please click the map or search an address to set a location.'
            )
          )
        } else if (!event.subject) {
          dispatch(setError('You must give the event a subject'))
        } else {
          const response = await fetch(`${BASE_URL}api/sigevents/${event.id}`, {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ ...event, error: undefined }),
          })
          if (response.ok) {
            dispatch(editEvent({ ...event, error: undefined }))
            goToList()
          } else {
            dispatch(setError(response.statusText))
          }
        }
      },
      searchAddress: async () => {
        if (ready && locator) {
          const locations = await locator.addressToLocations({
            address: {
              SingleLine: event && event.address,
            },
          })
          if (locations.length) {
            const addr = locations[0].address
            const location = locations[0].location
            dispatch(setPosition(location.longitude, location.latitude, addr))
          } else {
            dispatch(setError('Address not found.'))
          }
        }
      },
      zoomTo: () => {
        if (view && event.latitude && event.longitude) {
          const zoom = view.zoom > 15 ? view.zoom : 15
          view.goTo({
            center: [event.longitude, event.latitude],
            zoom,
          })
        }
      },
    }),
    [dispatch, token, event, ready, locator, view, goToList]
  )

  return [event, actions]
}

export const useSelectedSigEvent = () => useSelector(getCurrentSelectedSigEvent)

export const useSigEvents = () => {
  const events = useSelector(getEvents)
  return events
}

export const useSigEventsList = () => {
  const sortOrder = useUserPreference(PREFERENCES.SIGEVENT_DISPLAY_ORDER)
  const unsortedIds = useSelector(getSigEventIds)
  const byId = useSelector(getEventById)
  const isLoading = useSelector(getSigEventsIsLoading)
  const visibleIncidentIds = useSelector(getVisibleIncidentIds)

  let sortFunc = undefined
  if (sortOrder === 'created') {
    // by created
    sortFunc = (a, b) => {
      if (moment(a.createdDate).isSame(b.createdDate))
        return a.id < b.id ? -1 : 1
      return moment(a.createdDate).isBefore(b.createdDate) ? 1 : -1
    }
  } else {
    // by modified
    sortFunc = (a, b) => {
      if (moment(a.modifiedDate).isSame(b.modifiedDate))
        return a.id < b.id ? -1 : 1
      return moment(a.modifiedDate).isBefore(b.modifiedDate) ? 1 : -1
    }
  }

  const eventIds = unsortedIds
    .map(byId)
    .filter((e) => visibleIncidentIds.includes(e.incidentId))
    .sort(sortFunc)
    .map((e) => e.id)

  return [{ eventIds, isLoading }]
}

export const useIncidents = () => {
  const incidents = useSelector(getIncidents)
  return incidents
}

export const useVisibleIncidents = () => {
  const incidents = useSelector(getVisibleIncidents)
  return incidents
}

export const useIncidentIds = () => {
  const ids = useSelector(getIncidentIds)
  return ids
}

export const useIncident = (incidentId) => {
  const dispatch = useDispatch()
  const incident = useSelector(getIncidentById)(incidentId)

  const actions = useMemo(
    () => ({
      show: () => dispatch(showIncident(incidentId)),
      hide: () => dispatch(hideIncident(incidentId)),
    }),
    [dispatch, incidentId]
  )

  return [incident, actions]
}

export const useSigEventsMap = () => {
  const dispatch = useDispatch()
  const selectedIds = useSelector(getSelectedEventIds)
  const current = useSelector(getCurrentSelectedIndex)
  const sigEvents = useSelector(getVisibleEvents)
  const incidentsById = useSelector(getIncidentById)
  const addSelected = useIsSelected('/sigevents/addEvent')
  const editSelected = useIsSelected('/sigevents/editEvent')
  const editorIsOpen = addSelected || editSelected

  const select = useCallback((eventIds) => dispatch(selectEvents(eventIds)), [
    dispatch,
  ])
  const clear = useCallback(() => dispatch(clearSelection()), [dispatch])
  const selected = useMemo(() => ({ ids: selectedIds, current }), [
    selectedIds,
    current,
  ])
  return [
    { sigEvents, incidentsById, selected, editorIsOpen },
    { select, clear },
  ]
}

export const useSigEventsEditMap = () => {
  const dispatch = useDispatch()
  const sigEvent = useSelector(getEditor)
  const incident = useSelector(getIncidentById)(sigEvent.incidentId)
  const addSelected = useIsSelected('/sigevents/addEvent')
  const editSelected = useIsSelected('/sigevents/editEvent')
  const editorIsOpen = addSelected || editSelected

  const move = useCallback(
    (lng, lat, address) => {
      dispatch(setPosition(lng, lat, address))
    },
    [dispatch]
  )

  return [{ sigEvent, incident, editorIsOpen }, { move }]
}

// used by SigEventsDetailsPanel
export const useSigEventsHeader = () => {
  const dispatch = useDispatch()
  const event = useSelector(getCurrentSelectedSigEvent)
  const [{ view }] = useMap()
  const [_, select] = useTab('editEvent')

  const zoom = useCallback(() => {
    if (view && event.latitude && event.longitude) {
      const zoom = view.zoom > 15 ? view.zoom : 15
      view.goTo({
        center: [event.longitude, event.latitude],
        zoom,
      })
    }
  }, [view, event])

  const clear = useCallback(() => dispatch(clearSelection()), [dispatch])

  const edit = useCallback(() => {
    dispatch(openEditor(event))
    select()
  }, [dispatch, event, select])

  return { clear, zoom, edit }
}

export const useSigEventsPager = () => {
  const dispatch = useDispatch()
  const incidentCount = useSelector(getSelectedCount)
  const currentIndex = useSelector(getCurrentSelectedIndex)

  const next = useCallback(() => dispatch(nextEvent()), [dispatch])
  const prev = useCallback(() => dispatch(prevEvent()), [dispatch])

  return [
    { page: currentIndex + 1, totalPages: incidentCount },
    { next, prev },
  ]
}
